import React, { useState } from "react";
import { setProfile } from "@/lib/profileStore";
import CollegeSelect from "@/components/CollegeSelect";
import ResidencyToggle from "@/components/ResidencyToggle";

const INCOME = [
  { v: "under_30k", label: "Under $30,000" },
  { v: "30k_60k", label: "$30,000 – $60,000" },
  { v: "60k_100k", label: "$60,000 – $100,000" },
  { v: "100k_150k", label: "$100,000 – $150,000" },
  { v: "150k_250k", label: "$150,000 – $250,000" },
  { v: "over_250k", label: "Over $250,000" },
  { v: "prefer_not_say", label: "Prefer not to say" },
];

const STATES = [
  ["AL","AL"],["AK","AK"],["AZ","AZ"],["AR","AR"],["CA","CA"],["CO","CO"],["CT","CT"],["DE","DE"],
  ["DC","DC"],["FL","FL"],["GA","GA"],["HI","HI"],["ID","ID"],["IL","IL"],["IN","IN"],["IA","IA"],
  ["KS","KS"],["KY","KY"],["LA","LA"],["ME","ME"],["MD","MD"],["MA","MA"],["MI","MI"],["MN","MN"],
  ["MS","MS"],["MO","MO"],["MT","MT"],["NE","NE"],["NV","NV"],["NH","NH"],["NJ","NJ"],["NM","NM"],
  ["NY","NY"],["NC","NC"],["ND","ND"],["OH","OH"],["OK","OK"],["OR","OR"],["PA","PA"],["RI","RI"],
  ["SC","SC"],["SD","SD"],["TN","TN"],["TX","TX"],["UT","UT"],["VT","VT"],["VA","VA"],["WA","WA"],
  ["WV","WV"],["WI","WI"],["WY","WY"],
];

const PLAN_OPTIONS = [
  { v: "ED", label: "ED" },
  { v: "EA", label: "EA" },
  { v: "RD", label: "RD" },
  { v: "unknown", label: "?" },
];

/**
 * Inline profile form. Lives at the top of agent pages — when collapsed shows
 * a one-line "your basics" summary; expand to edit. Saves to localStorage.
 */
export default function ProfileForm({ profile, onChange, requireFields = [], compact = false }) {
  const [open, setOpen] = useState(!compact);
  const update = (patch) => { const next = { ...profile, ...patch }; onChange(next); setProfile(patch); };
  const colleges = profile.college_list || [];
  const updateCollege = (i, p) => update({ college_list: colleges.map((c, idx) => idx === i ? { ...c, ...p } : c) });
  const addCollege = () => update({ college_list: [...colleges, { name: "", plan: "unknown", residency: "" }] });
  const removeCollege = (i) => update({ college_list: colleges.filter((_, idx) => idx !== i) });

  if (!open) {
    return (
      <div className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-4 sm:p-5 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="profile-form-collapsed">
        <div className="text-xs text-slate-600">
          {profile.income_bracket ? <span>Income: <span className="text-[#0f172a]">{INCOME.find((b) => b.v === profile.income_bracket)?.label}</span></span> : <span className="text-slate-400">No income set</span>}
          {profile.home_state && <span> · State: <span className="text-[#0f172a]">{profile.home_state}</span></span>}
          {profile.gpa && <span> · GPA: <span className="text-[#0f172a]">{profile.gpa}</span></span>}
          {colleges.length > 0 && <span> · {colleges.length} school{colleges.length===1?"":"s"}</span>}
        </div>
        <button onClick={() => setOpen(true)} data-testid="profile-form-edit"
          className="text-xs text-[#1d4ed8] hover:underline">Edit</button>
      </div>
    );
  }

  return (
    <section className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-5 sm:p-6 mb-6" data-testid="profile-form-open">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading italic text-xl text-[#0f172a]">Your basics</h3>
        {compact && <button onClick={() => setOpen(false)} data-testid="profile-form-collapse" className="text-xs text-slate-400 hover:text-slate-700">collapse</button>}
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1">Home state</label>
          <select value={profile.home_state || ""} onChange={(e) => update({ home_state: e.target.value })}
            data-testid="profile-state"
            className="w-full rounded-xl border border-[#bfdbfe] px-3 py-2 bg-white text-sm">
            <option value="">—</option>
            {STATES.map(([code]) => (<option key={code} value={code}>{code}</option>))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1">GPA (optional)</label>
          <input type="number" step="0.01" min={0} max={5} value={profile.gpa ?? ""}
            onChange={(e) => update({ gpa: e.target.value })}
            data-testid="profile-gpa"
            placeholder="3.85"
            className="w-full rounded-xl border border-[#bfdbfe] px-3 py-2 text-sm focus:outline-none focus:border-[#1d4ed8]"/>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1">Application cycle</label>
          <select value={profile.application_cycle} onChange={(e) => update({ application_cycle: e.target.value })}
            data-testid="profile-cycle"
            className="w-full rounded-xl border border-[#bfdbfe] px-3 py-2 bg-white text-sm">
            <option value="2025-2026">2025–2026</option>
            <option value="2026-2027">2026–2027</option>
            <option value="2027-2028">2027–2028</option>
          </select>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-[11px] uppercase tracking-widest text-slate-500 mb-2">Family income range</label>
        <div className="flex flex-wrap gap-1.5">
          {INCOME.map((b) => (
            <button key={b.v} type="button" onClick={() => update({ income_bracket: b.v })}
              data-testid={`profile-income-${b.v}`}
              className={`text-xs rounded-full border px-3 py-1.5 transition ${
                profile.income_bracket === b.v
                  ? "border-[#1d4ed8] bg-[#eff6ff] text-[#0f172a]"
                  : "border-[#bfdbfe] bg-white text-slate-600 hover:border-[#1d4ed8]"
              }`}>{b.label}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-[11px] uppercase tracking-widest text-slate-500">Your college list</label>
          <button type="button" onClick={addCollege} data-testid="profile-add-college"
            className="text-xs text-[#1d4ed8] hover:underline">+ add</button>
        </div>
        <div className="space-y-2">
          {colleges.length === 0 && (
            <button type="button" onClick={addCollege}
              className="w-full rounded-xl border border-dashed border-[#bfdbfe] px-3 py-3 text-xs text-slate-500 hover:border-[#1d4ed8]">
              Add the first school you're considering
            </button>
          )}
          {colleges.map((c, i) => (
            <div key={i} className="rounded-xl border border-[#bfdbfe] p-3 space-y-3" data-testid={`profile-college-${i}`}>
              <div className="grid grid-cols-[1fr_auto_auto] gap-2">
                <CollegeSelect
                  value={c.name}
                  onChange={(name) => updateCollege(i, { name })}
                  placeholder="e.g. Princeton University"
                  inputClassName="w-full rounded-xl border border-[#bfdbfe] px-3 py-2 text-sm focus:outline-none focus:border-[#1d4ed8]"
                  testId={`profile-college-name-${i}`}
                />
                <select value={c.plan} onChange={(e) => updateCollege(i, { plan: e.target.value })}
                  data-testid={`profile-college-plan-${i}`}
                  className="rounded-xl border border-[#bfdbfe] px-2 py-2 bg-white text-sm">
                  {PLAN_OPTIONS.map((p) => (<option key={p.v} value={p.v}>{p.label}</option>))}
                </select>
                <button type="button" onClick={() => removeCollege(i)}
                  data-testid={`profile-college-remove-${i}`}
                  className="px-2 text-slate-400 hover:text-red-600 text-sm">×</button>
              </div>
              <ResidencyToggle
                value={c.residency || ""}
                onChange={(v) => updateCollege(i, { residency: v })}
                testId={`profile-college-residency-${i}`}
              />
            </div>
          ))}
        </div>
      </div>

      {requireFields.length > 0 && (
        <p className="text-[11px] text-slate-500 mt-3">
          Required: {requireFields.join(", ")}. Saved in this browser only.
        </p>
      )}
    </section>
  );
}
